import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useGTM } from '@eneco/tracking';

import { availableProducts, createSelectedProductsMap, isProductSelected } from '../helpers';
import { AvailableProducts, B2BLeadFlowContext } from '../types';

interface EcommerceEventItem {
  item_name: string;
  item_id: string;
  price: number;
  discount: number;
  item_brand: string;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_variant: string;
  item_list_name: string;
  index: number;
  quantity: number;
}

interface EcommerceEventPurchase {
  event: 'purchase';
  ecommerce: {
    transaction_id: string;
    items: EcommerceEventItem[];
  };
}

interface EcommerceEventAddToCart {
  event: 'add_to_cart';
  ecommerce: {
    items: EcommerceEventItem[];
  };
}

const productNames: Record<AvailableProducts, string> = {
  enc_slimterugleveren: 'Slim terugleveren',
  enc_slimmeenergiesturing: 'Slimme energiesturing',
  enc_batterij: 'Batterij',
  enc_elektrischladen: 'Elektrisch laden',
  enc_eboiler: 'E-boiler',
  enc_warmtepomp: 'Warmtepomp',
  enc_zonnepanelen: 'Zonnepanelen',
  enc_vastgoedverduurzamen: 'Vastgoed verduurzamen',
  enc_elektriciteitgas: 'Elektriciteit en gas',
  enc_ietsanders: 'Iets anders',
};

export const useFlowTracking = () => {
  const PRICE = 0;
  const DISCOUNT = 0;
  const ITEM_BRAND = 'Eneco';
  const ITEM_CATEGORY = 'zakelijk';
  const ITEM_CATEGORY2 = '';
  const ITEM_CATEGORY3 = '';
  const ITEM_CATEGORY4 = '';
  const ITEM_VARIANT = '';
  const ITEM_LIST_NAME = '';
  const QUANTITY = 1;
  const { useFlowSelector, useBasketSelector } = useFlowHooks<B2BLeadFlowContext>();
  const { pushToDataLayer } = useGTM();
  const basketId = useBasketSelector(state => state.context?.basketId);
  const selectedProducts = useFlowSelector(state => state.context.selectedProducts);
  const mapSelectedProductsToItems = (
    productsSelection: B2BLeadFlowContext['selectedProducts'] = selectedProducts,
  ): EcommerceEventItem[] => {
    const purchasedProducts = availableProducts.filter(product => isProductSelected(productsSelection, product));

    return purchasedProducts.map((product, index) => {
      return {
        item_name: productNames[product],
        item_id: product,
        price: PRICE,
        discount: DISCOUNT,
        item_brand: ITEM_BRAND,
        item_category: ITEM_CATEGORY,
        item_category2: ITEM_CATEGORY2,
        item_category3: ITEM_CATEGORY3,
        item_category4: ITEM_CATEGORY4,
        item_variant: ITEM_VARIANT,
        item_list_name: ITEM_LIST_NAME,
        index,
        quantity: QUANTITY,
      };
    });
  };

  const trackPurchase = (): void => {
    const event: EcommerceEventPurchase = {
      event: 'purchase',
      ecommerce: {
        transaction_id: String(basketId ?? ''),
        items: mapSelectedProductsToItems(),
      },
    };

    pushToDataLayer({ ecommerce: null });
    // JSON stringify && parse to filter out properties with undefined values
    pushToDataLayer(JSON.parse(JSON.stringify(event)));
  };

  const trackAddToCart = (products: AvailableProducts[]): void => {
    const selectedProductsMap = createSelectedProductsMap(products);

    const event: EcommerceEventAddToCart = {
      event: 'add_to_cart',
      ecommerce: {
        items: mapSelectedProductsToItems(selectedProductsMap),
      },
    };

    pushToDataLayer({ ecommerce: null });
    // JSON stringify && parse to filter out properties with undefined values
    pushToDataLayer(JSON.parse(JSON.stringify(event)));
  };

  return { trackPurchase, trackAddToCart };
};
